// Pure helpers describing WHICH backend a request goes to and HOW a failed request should
// be understood. No firebase / axios imports so this module is trivially unit-testable.

export const API_BASE_URLS = {
  publicProduction: "https://api.tulu.io",
  publicDevelopment: "https://api.dev.tulu.io",
  // Internal load balancers inside the VPC, reachable only over the WireGuard VPN.
  // The server accepts DB Interface writes only through these hosts.
  vpnProduction: "https://api-rw.tulu.io",
  vpnDevelopment: "https://api-rw.dev.tulu.io",
};

export const ACCESS_MODES = {
  readOnly: "readOnly",
  readWrite: "readWrite",
};

// Which Firebase Auth instance signs the request: the app-wide default one (Printer
// dashboard) or the per-tab DB Interface session (see getDbInterfaceAuth in firebase.js).
export const AUTHENTICATION_SCOPES = {
  default: "default",
  dbInterface: "dbInterface",
};

// Codes the server puts in `{code}` on 401/403 responses for the DB Interface.
export const API_FAILURE_CODES = {
  sessionExpired: "DB_INTERFACE_SESSION_EXPIRED",
  stepUpRequired: "DB_INTERFACE_STEP_UP_REQUIRED",
  vpnRequired: "DB_INTERFACE_VPN_REQUIRED",
  writeChannelNotConfigured: "DB_INTERFACE_WRITE_CHANNEL_NOT_CONFIGURED",
};

export const API_FAILURE_KINDS = {
  network: "network",
  timeout: "timeout",
  sessionExpired: "sessionExpired",
  stepUpRequired: "stepUpRequired",
  vpnRequired: "vpnRequired",
  http: "http",
};

export function createApiTarget({
  isDev = false,
  accessMode = ACCESS_MODES.readOnly,
  authenticationScope = AUTHENTICATION_SCOPES.default,
} = {}) {
  const useVpnHost = accessMode === ACCESS_MODES.readWrite;
  const baseUrl = useVpnHost
    ? (isDev ? API_BASE_URLS.vpnDevelopment : API_BASE_URLS.vpnProduction)
    : (isDev ? API_BASE_URLS.publicDevelopment : API_BASE_URLS.publicProduction);
  return { baseUrl, isDev: Boolean(isDev), accessMode, authenticationScope };
}

// getPost/getRequest historically took a boolean `isDev` (Printer dashboard still does).
// A boolean means "public host, default auth"; an object is used as-is.
export function resolveApiTarget(isDevOrApiTarget) {
  if (isDevOrApiTarget && typeof isDevOrApiTarget === "object") {
    return isDevOrApiTarget;
  }
  return createApiTarget({ isDev: Boolean(isDevOrApiTarget) });
}

export function classifyApiFailure(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const apiTarget = error?.config?.apiTarget || null;

  let kind = API_FAILURE_KINDS.http;
  if (!error?.response) {
    kind = error?.code === "ECONNABORTED" ? API_FAILURE_KINDS.timeout : API_FAILURE_KINDS.network;
  } else if (status === 401 && code === API_FAILURE_CODES.sessionExpired) {
    kind = API_FAILURE_KINDS.sessionExpired;
  } else if (status === 401 && code === API_FAILURE_CODES.stepUpRequired) {
    kind = API_FAILURE_KINDS.stepUpRequired;
  } else if (status === 403 && code === API_FAILURE_CODES.vpnRequired) {
    kind = API_FAILURE_KINDS.vpnRequired;
  }

  return {
    kind,
    status,
    code,
    apiTarget,
    message: error?.response?.data?.error || error?.response?.data?.message || error?.message,
  };
}

// Returns null for failures that are handled globally (the session gate signs the user
// out / opens the step-up prompt) so call sites do not show a second, confusing toast.
export function getUserFacingApiErrorMessage(error) {
  const failure = error?.apiFailure || classifyApiFailure(error);
  const isReadWriteTarget = failure.apiTarget?.accessMode === ACCESS_MODES.readWrite;

  switch (failure.kind) {
    case API_FAILURE_KINDS.sessionExpired:
    case API_FAILURE_KINDS.stepUpRequired:
      return null;
    case API_FAILURE_KINDS.network:
      return isReadWriteTarget
        ? "Cannot reach the read-write API (api-rw). Connect to the TULU VPN and retry."
        : "Cannot reach the API. Check your internet connection and retry.";
    case API_FAILURE_KINDS.timeout:
      return "The API did not answer in time. Check your connection (or VPN) and retry.";
    case API_FAILURE_KINDS.vpnRequired:
      return "Writes are only accepted through the VPN host. Use the read-write page while connected to the VPN.";
    default:
      return failure.message || "Request failed";
  }
}
